import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  AppBar, Toolbar, Box, Button, IconButton,
  Drawer, List, ListItemButton, ListItemText,
  Menu, MenuItem, Typography, Divider, GlobalStyles,
  ClickAwayListener, Fade, Chip, Skeleton,
} from "@mui/material";
import {
  Menu as MenuIcon, ChevronDown, GraduationCap, Building2, ArrowRight, X,
  LayoutGrid, Star, BookOpen, ChevronRight, Check, Link2,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSettings } from "../../context/SettingsContext";
import { useUserType } from "../../utils/userType";
import { getMenus } from "../../api/Menus";
import NavSearch from "./NavSearch";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const COURSES_API_URL = "https://app.netlabsv2.warals.in/api/courses/";
// Assumed public endpoint. The /api/admin/... one needs an auth token, so the navbar needs a public one.
const LABEL_CATEGORIES_API_URL = "https://app.netlabsv2.warals.in/api/public/label-categories/";
const MEDIA_BASE_URL = "https://app.netlabsv2.warals.in";

const BADGE_STYLES = {
  "new": { bg: "#16A34A", label: "New" },
  "best seller": { bg: "#EA580C", label: "Best Seller" },
  "popular": { bg: "#2563EB", label: "Popular" },
  "trending": { bg: "#16A34A", label: "Trending" },
  "most popular": { bg: "#EA580C", label: "Most Popular" },
};



// "Switch" and the CTA button are app behavior, not admin-editable content,
// so they stay hardcoded here.
const switchCards = [
  { value: "student", label: "I am a student", sub: "Explore Learning Paths", href: "/", icon: GraduationCap },
  { value: "business", label: "I am a business", sub: "Talk To Experts", href: "/", icon: Building2 },
];

function formatINR(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `₹${num.toLocaleString("en-IN")}`;
}

// Is this item visible to the current audience?
function isVisibleTo(item, isBusiness) {
  if (!item.isActive) return false;
  if (item.visibility === "ALL") return true;
  if (item.visibility === "STUDENT") return !isBusiness;
  if (item.visibility === "BUSINESS") return isBusiness;
  return true;
}

function isExternalUrl(url) {
  return typeof url === "string" && /^https?:\/\//i.test(url);
}

// Normalizes either a raw array response or a DRF-paginated
// { count, next, previous, results: [...] } response into a plain array.
function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

// Sort courses by backend `sequence` (ascending). A sequence of 0, empty or
// missing means "not set" and goes to the very end. Ties fall back to
// `order_on_homepage`, then the API's original order (sort is stable).
function sortBySequence(list) {
  const seq = (c) => {
    const n = Number(c.sequence);
    return n > 0 ? n : Infinity;
  };
  return list.slice().sort((a, b) => {
    const sa = seq(a);
    const sb = seq(b);
    if (sa !== sb) return sa < sb ? -1 : 1;
    return (Number(a.order_on_homepage) || 0) - (Number(b.order_on_homepage) || 0);
  });
}

function GradientBorderButton({ children, sx, ...props }) {
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "999px",
        p: "1.5px",
        overflow: "hidden",
        flexShrink: 0,
        isolation: "isolate",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "300%",
          height: "300%",
          background:
            "conic-gradient(from 0deg, transparent 0%, transparent 78%, #93C5FD 86%, #2563EB 90%, #93C5FD 94%, transparent 100%)",
          transform: "translate(-50%, -50%) rotate(0deg)",
          animation: "borderRotate 2.8s linear infinite",
          zIndex: 0,
        },
      }}
    >
      <Button
        disableElevation
        {...props}
        sx={{
          position: "relative",
          zIndex: 1,
          bgcolor: "white",
          color: "#0F172A",
          borderRadius: "999px",
          fontWeight: 700,
          fontSize: 14,
          textTransform: "none",
          px: 2.75,
          py: 1,
          whiteSpace: "nowrap",
          "&:hover": { bgcolor: "#F8FAFC" },
          ...sx,
        }}
      >
        {children}
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Course card used inside the "All Courses" mega menu
// ---------------------------------------------------------------------------
function CourseCard({ course, onNavigate }) {
  const [imgError, setImgError] = useState(false);
  const badgeKey = (course.label || "").trim().toLowerCase();
  const badge = BADGE_STYLES[badgeKey];
  const hasDiscount = course.discount_price && Number(course.discount_price) < Number(course.price);
  const imgSrc = course.card_image_url
    ? (course.card_image_url.startsWith("http") ? course.card_image_url : `${MEDIA_BASE_URL}${course.card_image_url}`)
    : null;

  return (
    <Box
      onClick={() => onNavigate(`/courses/${course.slug}`)}
      sx={{
        position: "relative",
        border: "1px solid rgba(15,23,42,0.08)",
        borderRadius: 3,
        overflow: "hidden",
        cursor: "pointer",
        bgcolor: "white",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: "0 12px 28px -10px rgba(15,23,42,0.18)",
          transform: "translateY(-2px)",
        },
      }}
    >
    <Box
  sx={{
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    bgcolor: "#0B2A5B",
    overflow: "hidden",
  }}
>
  {imgSrc && !imgError ? (
    <Box
      component="img"
      src={imgSrc}
      alt={course.title}
      onError={() => setImgError(true)}
      sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
    />
  ) : (
    <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", bgcolor: "#F1F5F9" }}>
      <BookOpen size={28} />
    </Box>
  )}
  {badge && (
    <Chip
      label={badge.label}
      size="small"
      sx={{ position: "absolute", top: 8, right: 8, bgcolor: badge.bg, color: "white", fontWeight: 700, fontSize: 10.5, height: 20 }}
    />
  )}
</Box>

      <Box sx={{ p: 1.75, display: "flex", flexDirection: "column", gap: 0.75, flex: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "primary.main" }}>
          {course.label}
        </Typography>

        <Typography
          sx={{
            fontSize: 14.5, fontWeight: 700, color: "#0F172A", lineHeight: 1.35,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
            overflow: "hidden", minHeight: 38,
          }}
        >
          {course.title}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Chip label={course.level} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 600, bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB" }} />
          <Chip label={course.mode} size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 600, bgcolor: "rgba(15,23,42,0.05)", color: "text.secondary" }} />
          {Number(course.rating) > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, ml: "auto" }}>
              <Star size={12} fill="#F59E0B" color="#F59E0B" />
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#0F172A" }}>{Number(course.rating).toFixed(1)}</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: "auto", pt: 0.5 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0F172A" }}>
            {formatINR(hasDiscount ? course.discount_price : course.price)}
          </Typography>
          {hasDiscount && (
            <Typography sx={{ fontSize: 12, color: "text.secondary", textDecoration: "line-through" }}>
              {formatINR(course.price)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// "All Courses" mega menu panel — trigger is now admin-editable (COURSESMEGA
// menu item) but this fetch/render logic is unchanged.
// ---------------------------------------------------------------------------
function AllCoursesMegaMenu({ open, onClose, topOffset }) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [labelCategories, setLabelCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const fetchedRef = useRef(false);

  const fetchCourses = useCallback(async () => {
  setLoading(true);
  setError(null);
  try {
    const [coursesRes, labelsRes] = await Promise.all([
      fetch(COURSES_API_URL),
      // a failed labels request shouldn't break the courses list
      fetch(LABEL_CATEGORIES_API_URL).catch(() => null),
    ]);

    if (!coursesRes.ok) throw new Error(`Request failed with status ${coursesRes.status}`);
    const coursesData = await coursesRes.json();
setCourses(sortBySequence(toArray(coursesData)));

    if (labelsRes?.ok) {
      const labelsJson = await labelsRes.json();
      // handles { success, message, data: [] }, { results: [] }, or a plain array
      const list = Array.isArray(labelsJson)
        ? labelsJson
        : Array.isArray(labelsJson?.data)
          ? labelsJson.data
          : Array.isArray(labelsJson?.results)
            ? labelsJson.results
            : [];
      setLabelCategories(list);
    }
    fetchedRef.current = true;
  } catch (err) {
    setError("Couldn't load courses right now. Please try again.");
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    if (open && !fetchedRef.current) {
      fetchCourses();
    }
  }, [open, fetchCourses]);

  if (!open) return null;

// Sidebar categories come from the label-categories API, ordered by `order`.
// If that API returns nothing, fall back to the labels found on the courses.
// Sidebar shows only the categories returned by the API, ordered by `order`
const categories = labelCategories
  .slice()
  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  .map((c) => c.label);
  
  const filteredCourses = selectedCategory === "all" ? courses : courses.filter((c) => c.label === selectedCategory);

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Fade in={open}>
        <Box
          sx={{
            position: "fixed", top: topOffset, left: 0, right: 0, bgcolor: "white",
            borderTop: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 24px 48px -16px rgba(15,23,42,0.2)",
            zIndex: 1201, maxHeight: `calc(100vh - ${topOffset}px)`, overflow: "hidden",
          }}
        >
          <Box sx={{ maxWidth: 1280, mx: "auto", display: "flex", maxHeight: `calc(100vh - ${topOffset}px)` }}>
            <Box sx={{ width: 260, flexShrink: 0, borderRight: "1px solid rgba(0,0,0,0.06)", py: 2, overflowY: "auto" }}>
              <Typography sx={{ px: 2.5, pb: 1, fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", color: "text.secondary", textTransform: "uppercase" }}>
                Categories
              </Typography>

              <ListItemButton
                selected={selectedCategory === "all"}
                onClick={() => setSelectedCategory("all")}
                sx={{ px: 2.5, py: 1.1, borderLeft: "3px solid transparent", "&.Mui-selected": { borderLeft: "3px solid #2563EB", bgcolor: "rgba(37,99,235,0.06)" }, "&.Mui-selected:hover": { bgcolor: "rgba(37,99,235,0.09)" } }}
              >
                <ListItemText primary="All Courses" primaryTypographyProps={{ fontWeight: 700, fontSize: 14, color: selectedCategory === "all" ? "#2563EB" : "#0F172A" }} />
                <ChevronRight size={16} color={selectedCategory === "all" ? "#2563EB" : "#CBD5E1"} />
              </ListItemButton>

              {loading && categories.length === 0 && (
                <Box sx={{ px: 2.5, pt: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="text" width="80%" height={20} />)}
                </Box>
              )}

              {categories.map((cat) => (
                <ListItemButton
                  key={cat}
                  selected={selectedCategory === cat}
                  onClick={() => setSelectedCategory(cat)}
                  sx={{ px: 2.5, py: 1.1, borderLeft: "3px solid transparent", "&.Mui-selected": { borderLeft: "3px solid #2563EB", bgcolor: "rgba(37,99,235,0.06)" }, "&.Mui-selected:hover": { bgcolor: "rgba(37,99,235,0.09)" } }}
                >
                  <ListItemText primary={cat} primaryTypographyProps={{ fontWeight: 700, fontSize: 14, color: selectedCategory === cat ? "#2563EB" : "#0F172A" }} />
                  <ChevronRight size={16} color={selectedCategory === cat ? "#2563EB" : "#CBD5E1"} />
                </ListItemButton>
              ))}
            </Box>

            <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0F172A" }}>
                    {selectedCategory === "all" ? "All Courses" : selectedCategory}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                    Explore certifications and programs to advance your career
                  </Typography>
                </Box>
                <Button onClick={() => handleNavigate("/learn")} endIcon={<ArrowRight size={15} />} sx={{ textTransform: "none", fontWeight: 700, fontSize: 13.5 }}>
                  View all
                </Button>
              </Box>

              {error && (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 1.5 }}>{error}</Typography>
                  <Button variant="outlined" size="small" onClick={fetchCourses}>Retry</Button>
                </Box>
              )}

              {!error && loading && (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 2 }}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Box key={i} sx={{ border: "1px solid rgba(15,23,42,0.08)", borderRadius: 3, overflow: "hidden" }}>
                     <Skeleton variant="rectangular" sx={{ width: "100%", height: "auto", aspectRatio: "16 / 9" }} />
                      <Box sx={{ p: 1.75 }}>
                        <Skeleton variant="text" width="40%" />
                        <Skeleton variant="text" width="90%" />
                        <Skeleton variant="text" width="60%" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {!error && !loading && filteredCourses.length === 0 && (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>No courses found in this category yet.</Typography>
                </Box>
              )}

              {!error && !loading && filteredCourses.length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 2 }}>
                  {filteredCourses.map((course) => (
                    <CourseCard key={course.id} course={course} onNavigate={handleNavigate} />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>
    </ClickAwayListener>
  );
}

// ---------------------------------------------------------------------------
// A single admin-defined LINK item, rendered as either an internal RouterLink
// or an external <a>, inside a MUI Menu (dropdown) or drawer list.
// ---------------------------------------------------------------------------
function LinkMenuItem({ item, onClick }) {
  const external = isExternalUrl(item.url);
  const commonSx = { py: 1.5, px: 1.5, borderRadius: 2, "&:hover": { bgcolor: "rgba(37,99,235,0.06)" } };
  if (external) {
    return (
      <MenuItem component="a" href={item.url} target={item.openNewTab ? "_blank" : undefined} rel="noopener noreferrer" onClick={onClick} sx={commonSx}>
        <Typography variant="body2" fontWeight={700} color="#0F172A">{item.label}</Typography>
      </MenuItem>
    );
  }
  return (
    <MenuItem component={Link} to={item.url || "/"} onClick={onClick} sx={commonSx}>
      <Typography variant="body2" fontWeight={700} color="#0F172A">{item.label}</Typography>
    </MenuItem>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const settings = useSettings();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [businessAnchorEl, setBusinessAnchorEl] = useState(null); // reused per-dropdown anchor via id map below
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [switchAnchorEl, setSwitchAnchorEl] = useState(null);
  const [coursesMenuOpen, setCoursesMenuOpen] = useState(false);
  const [mobileOpenIds, setMobileOpenIds] = useState({});
  const [userType, setUserType] = useUserType();

  const isBusiness = userType === "business";

  // -------------------------------------------------------------------
  // Dynamic navbar data
  // -------------------------------------------------------------------
  const { data: allMenus = [], isLoading: menusLoading } = useQuery({
    queryKey: ["public-menus"],
    queryFn: () => getMenus().then((r) => toArray(r.data)),
    staleTime: 5 * 60 * 1000,
  });

  const topLevelMenus = allMenus
    .filter((m) => !m.parent && isVisibleTo(m, isBusiness))
    .sort((a, b) => a.order - b.order);

  const childrenOf = (id) =>
    allMenus
      .filter((m) => m.parent === id && isVisibleTo(m, isBusiness))
      .sort((a, b) => a.order - b.order);

  // NOTE: "COURSESMEGA" has no underscore — must match the backend's exact
  // enum spelling from the Menu API Frontend Integration Guide. If multiple
  // active COURSESMEGA items exist, only the first (post-sort) is used, per spec.
  const coursesMegaItem = topLevelMenus.find((m) => m.linkType === "COURSESMEGA");
  const otherTopLevelMenus = topLevelMenus.filter((m) => m.linkType !== "COURSESMEGA");

  const handleSwitch = (value, href) => {
    setUserType(value);
    setSwitchAnchorEl(null);
    navigate(href);
  };

  const openDropdown = (e, id) => {
    setOpenDropdownId(id);
    setDropdownAnchorEl(e.currentTarget);
  };
  const closeDropdown = () => {
    setOpenDropdownId(null);
    setDropdownAnchorEl(null);
  };

  const toggleMobileGroup = (id) => setMobileOpenIds((s) => ({ ...s, [id]: !s[id] }));

  const TOOLBAR_HEIGHT = 84;

  const logoUrl = settings.logo_url || "/logo/logo.png";
  const siteName = settings.site_name || "NetLabs ITS";
  const tagline = settings.company_tagline || "Quality Gives Confidence";

  return (
    <>
      <GlobalStyles
        styles={{
          "@keyframes borderRotate": {
            "0%": { transform: "translate(-50%, -50%) rotate(0deg)" },
            "100%": { transform: "translate(-50%, -50%) rotate(360deg)" },
          },
        }}
      />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{ bgcolor: "rgba(255,255,255,0.86)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(0,0,0,0.06)", color: "text.primary" }}
      >
        <Toolbar sx={{ maxWidth: 1280, width: "100%", mx: "auto", px: { xs: 1, md: 3 }, height: TOOLBAR_HEIGHT }}>
          <Box
            component={Link}
            to="/"
            sx={{ display: "flex", alignItems: "center", gap: 1.75, textDecoration: "none", mr: { xs: 3, lg: 3.5 }, py: 1, flexShrink: 0, transition: "opacity 0.2s ease", "&:hover": { opacity: 0.85 } }}
          >
            <Box component="img" src={logoUrl} alt={siteName} sx={{ height: { xs: 48, md: 64 }, width: "auto", flexShrink: 0 }} />
            <Box sx={{ display: { xs: "none", md: "block" }, borderLeft: "1px solid rgba(15,23,42,0.1)", pl: 1.75, minWidth: 0, maxWidth: 225, flexShrink: 1 }}>
              <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: "text.primary", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                ISO 9001:2015 Certified
              </Typography>
              <Typography noWrap sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", color: "primary.main", textTransform: "uppercase", lineHeight: 1.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {tagline}
              </Typography>
            </Box>
          </Box>

          {/* All Courses trigger — admin-controlled via a COURSESMEGA menu item */}
          {coursesMegaItem && (
            <Box sx={{ display: { xs: "none", lg: "block" }, mr: 2.5, flexShrink: 0 }}>
              <Button
                onClick={() => setCoursesMenuOpen((v) => !v)}
                disableElevation
                startIcon={<LayoutGrid size={17} />}
                endIcon={<ChevronDown size={15} style={{ transform: coursesMenuOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />}
                sx={{
                  bgcolor: coursesMenuOpen ? "#1D4ED8" : "#2563EB", color: "white", borderRadius: "999px",
                  fontWeight: 700, fontSize: 13.5, textTransform: "none", px: 2.25, py: 1, whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#1D4ED8" },
                }}
              >
                {coursesMegaItem.label}
              </Button>
            </Box>
          )}

          <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 2.5, flexShrink: 0 }}>
            {menusLoading && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <Skeleton variant="text" width={70} height={24} />
                <Skeleton variant="text" width={70} height={24} />
              </Box>
            )}

            {!menusLoading && otherTopLevelMenus.map((item) => {
              if (item.linkType === "DROPDOWN") {
                const children = childrenOf(item.id);
                if (children.length === 0) return null;
                const open = openDropdownId === item.id;
                return (
                  <React.Fragment key={item.id}>
                    <Box
                      sx={{
                        display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer",
                        fontSize: 15, fontWeight: 700, whiteSpace: "nowrap",
                        color: open ? "primary.main" : "#0F172A",
                        "&:hover": { color: "primary.main" }, transition: "color 0.2s",
                      }}
                      onClick={(e) => openDropdown(e, item.id)}
                    >
                      {item.label}
                      <ChevronDown size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                    </Box>
                    <Menu
                      anchorEl={open ? dropdownAnchorEl : null}
                      open={open}
                      onClose={closeDropdown}
                      PaperProps={{ sx: { borderRadius: 3, mt: 1, minWidth: 270, boxShadow: "0 20px 48px -12px rgba(15,23,42,0.18)", p: 0.5 } }}
                    >
                      {children.map((child) => (
                        <LinkMenuItem key={child.id} item={child} onClick={closeDropdown} />
                      ))}
                    </Menu>
                  </React.Fragment>
                );
              }

              // Plain top-level LINK item
              const external = isExternalUrl(item.url);
              const active = !external && location.pathname === item.url;
              const linkSx = {
                position: "relative", fontSize: 15, fontWeight: 700, textDecoration: "none",
                color: active ? "primary.main" : "#0F172A", py: 0.5, whiteSpace: "nowrap",
                "&:hover": { color: "primary.main" }, transition: "color 0.2s",
                "&::after": {
                  content: '""', position: "absolute", left: 0, right: 0, bottom: -2, height: "2px",
                  borderRadius: "2px", bgcolor: "primary.main",
                  transform: active ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform 0.2s ease",
                },
                "&:hover::after": { transform: "scaleX(1)" },
              };
              return external ? (
                <Box key={item.id} component="a" href={item.url} target={item.openNewTab ? "_blank" : undefined} rel="noopener noreferrer" sx={linkSx}>
                  {item.label}
                </Box>
              ) : (
                <Box key={item.id} component={Link} to={item.url || "/"} sx={linkSx}>
                  {item.label}
                </Box>
              );
            })}

            {/* Switch Dropdown — app behavior, not admin content */}
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer",
                fontSize: 15, fontWeight: 700, whiteSpace: "nowrap",
                color: Boolean(switchAnchorEl) ? "primary.main" : "#0F172A",
                "&:hover": { color: "primary.main" }, transition: "color 0.2s",
              }}
              onClick={(e) => setSwitchAnchorEl(e.currentTarget)}
            >
              Switch
              <ChevronDown size={16} style={{ transform: Boolean(switchAnchorEl) ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            </Box>
            <Menu
              anchorEl={switchAnchorEl}
              open={Boolean(switchAnchorEl)}
              onClose={() => setSwitchAnchorEl(null)}
              PaperProps={{ sx: { borderRadius: 3, mt: 1, minWidth: 270, boxShadow: "0 20px 48px -12px rgba(15,23,42,0.18)", p: 0.5 } }}
            >
              {switchCards.map(({ value, label, sub, href, icon: Icon }) => {
                const active = userType === value;
                return (
                  <MenuItem
                    key={label}
                    onClick={() => handleSwitch(value, href)}
                    sx={{ gap: 1.5, py: 1.5, px: 1.5, borderRadius: 2, bgcolor: active ? "rgba(37,99,235,0.06)" : "transparent", "&:hover": { bgcolor: "rgba(37,99,235,0.06)" } }}
                  >
                    <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon size={18} strokeWidth={2.1} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={700} color="#0F172A">{label}</Typography>
                      <Typography variant="caption" color="text.secondary">{sub}</Typography>
                    </Box>
                    {active && <Check size={17} color="#2563EB" />}
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>

          <Box sx={{ flex: "1 1 0%", pl: { xs: 1, lg: 3 }, display: "flex", alignItems: "center", gap: { xs: 1, lg: 2 }, minWidth: 0 }}>
            <Box sx={{ flexGrow: { xs: 0, lg: 1 }, minWidth: 0, maxWidth: { lg: 640 } }}>
              <NavSearch fullWidth />
            </Box>

            <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", flexShrink: 0 }}>
              <GradientBorderButton component="a" href="#contact">
                Talk To Experts
              </GradientBorderButton>
            </Box>

            <Box sx={{ display: { lg: "none" }, flexShrink: 0 }}>
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "10px" }}>
                <MenuIcon size={22} />
              </IconButton>
            </Box>
          </Box>
        </Toolbar>

        {coursesMegaItem && (
          <AllCoursesMegaMenu open={coursesMenuOpen} onClose={() => setCoursesMenuOpen(false)} topOffset={TOOLBAR_HEIGHT} />
        )}

        {/* Mobile Drawer */}
        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 280, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2.5 }}>
              <Box component="img" src={logoUrl} alt={siteName} sx={{ height: 40, width: "auto" }} />
              <IconButton onClick={() => setDrawerOpen(false)} size="small">
                <X size={20} />
              </IconButton>
            </Box>
            <Divider />

            <Box sx={{ display: "flex", gap: 1, p: 1.5 }}>
              {switchCards.map(({ value, label, href, icon: Icon }) => {
                const active = userType === value;
                return (
                  <Box
                    key={value}
                    onClick={() => handleSwitch(value, href)}
                    sx={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, py: 1,
                      borderRadius: "10px", cursor: "pointer", fontSize: 12.5, fontWeight: 700,
                      border: active ? "1.5px solid #2563EB" : "1.5px solid rgba(15,23,42,0.1)",
                      color: active ? "#2563EB" : "text.secondary", bgcolor: active ? "rgba(37,99,235,0.06)" : "transparent",
                    }}
                  >
                    <Icon size={15} />
                    {label}
                  </Box>
                );
              })}
            </Box>
            <Divider />

            <List sx={{ px: 1, py: 1.5, flex: 1, overflowY: "auto" }}>
              {coursesMegaItem && (
                <ListItemButton
                  component={Link}
                  to="/courses"
                  onClick={() => setDrawerOpen(false)}
                  sx={{ borderRadius: 2, mb: 0.5, bgcolor: "rgba(37,99,235,0.08)" }}
                >
                  <LayoutGrid size={17} style={{ marginRight: 10 }} color="#2563EB" />
                  <ListItemText primary={coursesMegaItem.label} primaryTypographyProps={{ fontWeight: 700, fontSize: 15, color: "#2563EB" }} />
                </ListItemButton>
              )}

              {menusLoading && (
                <Box sx={{ px: 2, py: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="text" width="70%" height={22} />)}
                </Box>
              )}

              {!menusLoading && otherTopLevelMenus.map((item) => {
                if (item.linkType === "DROPDOWN") {
                  const children = childrenOf(item.id);
                  if (children.length === 0) return null;
                  const isOpen = !!mobileOpenIds[item.id];
                  return (
                    <React.Fragment key={item.id}>
                      <ListItemButton onClick={() => toggleMobileGroup(item.id)} sx={{ borderRadius: 2, mt: 0.5 }}>
                        <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 700, fontSize: 15 }} />
                        <ChevronDown size={16} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                      </ListItemButton>
                      {isOpen && children.map((child) => {
                        const external = isExternalUrl(child.url);
                        const active = !external && location.pathname + location.search === child.url;
                        const commonSx = { borderRadius: 2, mb: 0.5, color: active ? "primary.main" : "text.primary", bgcolor: active ? "rgba(37,99,235,0.07)" : "transparent", pl: 4 };
                        return external ? (
                          <ListItemButton
                            key={child.id}
                            component="a"
                            href={child.url}
                            target={child.openNewTab ? "_blank" : undefined}
                            rel="noopener noreferrer"
                            onClick={() => setDrawerOpen(false)}
                            sx={commonSx}
                          >
                            <ListItemText primary={child.label} primaryTypographyProps={{ fontWeight: 700, fontSize: 14 }} />
                          </ListItemButton>
                        ) : (
                          <ListItemButton key={child.id} component={Link} to={child.url || "/"} onClick={() => setDrawerOpen(false)} sx={commonSx}>
                            <ListItemText primary={child.label} primaryTypographyProps={{ fontWeight: 700, fontSize: 14 }} />
                          </ListItemButton>
                        );
                      })}
                    </React.Fragment>
                  );
                }

                const external = isExternalUrl(item.url);
                const active = !external && location.pathname === item.url;
                const commonSx = { borderRadius: 2, mb: 0.5, color: active ? "primary.main" : "text.primary", bgcolor: active ? "rgba(37,99,235,0.07)" : "transparent" };
                return external ? (
                  <ListItemButton
                    key={item.id}
                    component="a"
                    href={item.url}
                    target={item.openNewTab ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    onClick={() => setDrawerOpen(false)}
                    sx={commonSx}
                  >
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 700, fontSize: 15 }} />
                  </ListItemButton>
                ) : (
                  <ListItemButton key={item.id} component={Link} to={item.url || "/"} onClick={() => setDrawerOpen(false)} sx={commonSx}>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 700, fontSize: 15 }} />
                  </ListItemButton>
                );
              })}
            </List>
            <Divider />
            <Box sx={{ p: 2.5 }}>
              <Button
                component="a"
                href="#contact"
                variant="contained"
                fullWidth
                endIcon={<ArrowRight size={16} />}
                onClick={() => setDrawerOpen(false)}
                sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none", bgcolor: "#2563EB", py: 1.2, "&:hover": { bgcolor: "#1D4ED8" } }}
              >
                Talk To Experts
              </Button>
            </Box>
          </Box>
        </Drawer>
      </AppBar>
    </>
  );
}